/**
 * Configuration
 * Application constants and configuration
 */

export const CONFIG = {
    // API Configuration
    api: {
        getBaseURL() {
            return window.CAMPAIGNS_CONFIG.DISCOVERY_API_URL;
        },
        getPublicURL() {
            return window.location.origin;
        }
    },

    // Project definitions
    projects: {
        'skia-image-mcp': {
            title: 'Skia Image MCP',
            description: 'AI image generation MCP server for Claude Desktop & ChatGPT',
            adminOnly: true,
            productFile: 'skia-image-mcp.json'
        },
        'assessment-api': {
            title: 'Assessment API',
            description: 'Intelligent assessment and scoring API for lead qualification',
            adminOnly: true,
            productFile: 'assessment-api.json'
        }
    },

    // Questions for discovery
    questions: [
        {
            key: 'selling',
            text: "Great! Let's start with the basics. What exactly are you selling with {project}?",
            quickReplies: [
                "The product itself",
                "Managed service",
                "Consulting/coaching",
                "Let me explain..."
            ]
        },
        {
            key: 'businessModel',
            text: "Got it. Now, how does the business model work? How do customers pay?",
            quickReplies: [
                "One-time purchase",
                "Monthly subscription",
                "Usage-based pricing",
                "Tiered plans",
                "Let me explain..."
            ]
        },
        {
            key: 'buyer',
            text: "Perfect. Who's the target buyer making the purchasing decision?",
            quickReplies: [
                "Individuals",
                "Small businesses",
                "Agencies",
                "Enterprise teams",
                "Someone else..."
            ]
        },
        {
            key: 'brandName',
            text: "Excellent. What's the brand/product name customers will see?",
            quickReplies: []
        },
        {
            key: 'tagline',
            text: "Nice name! Do you have a tagline or positioning statement? (optional - you can skip)",
            quickReplies: ["Skip this for now"]
        },
        {
            key: 'offer',
            text: "What's your offer? What do they get when they take action? (e.g., Free call, $29/month, Free trial)",
            quickReplies: ["Skip offer for now"]
        },
        {
            key: 'uniqueValue',
            text: "What makes this different or better than alternatives? What's the unique value?",
            quickReplies: ["Let me think and type it out..."]
        },
        {
            key: 'constraints',
            text: "Last question: Are there any constraints or things we should NOT say in the campaign?",
            quickReplies: [
                "No constraints",
                "Let me specify..."
            ]
        }
    ],

    // System prompt for Claude
    systemPrompt: `You are a campaign discovery assistant helping to gather information for creating high-converting marketing campaigns using the Enemy-Named PAS framework.

Your role is to ask strategic questions to understand:
1. What they're selling (product, service, feature, add-on)
2. Business model (pricing, payment structure)
3. Target buyer (who makes the purchase decision)
4. Brand name and positioning
5. Unique value proposition
6. Constraints (what NOT to say)

Be conversational, friendly, and concise. Ask one question at a time. Acknowledge their answers before moving to the next question.

After gathering all information, summarize what you learned in a structured format.`
};

/**
 * NEXUS Mode Configuration
 * Handles integration when Campaigns is embedded in BIQ-NEXUS
 *
 * UNIFIED IDENTITY (MP-05):
 * When TRUST_NEXUS_JWT is enabled (or when embedded), Campaigns trusts
 * the AUTH_TOKEN message from NEXUS and uses JWT claims for user context.
 */
export const NexusConfig = {
    /**
     * Unified Identity: User context from JWT (MP-05)
     * Populated when AUTH_TOKEN is received and processed
     */
    nexusUser: null,

    /**
     * Feature flag for unified identity (MP-05)
     * When true, trusts JWT from NEXUS instead of local auth
     * Auto-enabled when running in iframe (embedded mode)
     */
    trustNexusJwt: false,

    /**
     * Callback registered to fire when AUTH_TOKEN is processed
     */
    _onAuthReady: null,

    /**
     * Check if running in NEXUS mode
     * Detects via URL parameter or sessionStorage persistence
     */
    isNexusMode() {
        const urlParam = new URLSearchParams(window.location.search).get('mode') === 'nexus';
        const sessionFlag = sessionStorage.getItem('nexus_mode') === 'true';
        return urlParam || sessionFlag;
    },

    /**
     * Check if running in Standalone mode (embedded via Quick Access)
     * Standalone = full features but no profile access (embedded context)
     * Detects via URL parameter or sessionStorage persistence
     */
    isStandaloneMode() {
        const urlParam = new URLSearchParams(window.location.search).get('mode') === 'standalone';
        const sessionFlag = sessionStorage.getItem('standalone_mode') === 'true';
        return urlParam || sessionFlag;
    },

    /**
     * Initialize NEXUS mode - persist to sessionStorage for page navigation
     * Also sets up AUTH_TOKEN listener for unified identity (MP-05)
     */
    init() {
        // MP-02 fix — mode arbitration (mirrors the dashboard). An explicit ?mode= param
        // WINS and clears the opposing sticky flag, so a standalone/expert or no-param
        // load after a prior nexus session no longer resolves to nexus. A no-param load
        // that is NOT a genuine iframe embed clears any stale nexus flag (direct/bookmark
        // = expert), while an in-iframe no-param nav keeps the sticky flag.
        const urlParams = new URLSearchParams(window.location.search);
        const urlMode = urlParams.get('mode');
        const inIframe = (() => { try { return window.self !== window.top; } catch (e) { return true; } })();

        if (urlMode === 'nexus') {
            sessionStorage.setItem('nexus_mode', 'true');
            sessionStorage.removeItem('standalone_mode');
        } else if (urlMode === 'standalone') {
            sessionStorage.setItem('standalone_mode', 'true');
            sessionStorage.removeItem('nexus_mode');
        } else if (!inIframe) {
            // Direct/bookmark load with no mode param — do not let a stale nexus flag stick.
            sessionStorage.removeItem('nexus_mode');
        }

        // Enable unified identity JWT trust when embedded (MP-05)
        if (this.isEmbedded()) {
            this.trustNexusJwt = true;
            this._initAuthTokenListener();
            console.log('[NexusConfig] Unified identity enabled - waiting for AUTH_TOKEN');
        }

        return this.isNexusMode();
    },

    /**
     * Clear NEXUS mode (for testing or explicit exit)
     */
    clear() {
        sessionStorage.removeItem('nexus_mode');
        sessionStorage.removeItem('standalone_mode');
        this.nexusUser = null;
    },

    /**
     * Initialize AUTH_TOKEN message listener (MP-05)
     * Listens for AUTH_TOKEN from parent NEXUS window
     */
    _initAuthTokenListener() {
        window.addEventListener('message', (event) => {
            // Validate message structure
            const message = event.data;
            if (!message || typeof message.type !== 'string') return;
            if (message.source === 'campaigns') return; // Ignore our own messages

            if (message.type === 'AUTH_TOKEN') {
                this._handleAuthToken(message.payload);
            } else if (message.type === 'THEME_CHANGE') {
                this._handleThemeChange(message.payload);
            }
        });
    },

    /**
     * Handle AUTH_TOKEN message from NEXUS (MP-05)
     * Decodes JWT and stores user context
     * @param {Object} payload - { accessToken: string }
     */
    _handleAuthToken(payload) {
        if (!payload || !payload.accessToken) {
            console.error('[NexusConfig] AUTH_TOKEN received but no accessToken in payload');
            this.sendToParent('ERROR', { message: 'No access token provided' });
            return;
        }

        try {
            // Decode JWT (no signature validation in browser - trust is established by origin)
            const claims = this._decodeJwt(payload.accessToken);

            // Store unified identity context (MP-05)
            this.nexusUser = {
                userId: claims.userId || claims.sub,
                email: claims.email,
                name: claims.name,
                role: claims.role,
                organizationId: claims.organizationId || claims.orgId,
                orgName: claims.orgName,
                ghlLocationId: claims.ghlLocationId,
                enabledApps: claims.enabledApps || [],
                canSwitchAccounts: claims.canSwitchAccounts || false,
                subscriptionTier: claims.subscriptionTier,
                // Impersonation context (MP-02)
                isImpersonating: !!claims.impersonatingUserId,
                impersonatingUserId: claims.impersonatingUserId,
                impersonatingOrgId: claims.impersonatingOrgId,
                originalUserId: claims.originalUserId,
                // Mark as unified identity session
                unifiedIdentity: true
            };

            console.log('[NexusConfig] AUTH_TOKEN processed - unified identity active:', {
                email: this.nexusUser.email,
                orgId: this.nexusUser.organizationId,
                ghlLocationId: this.nexusUser.ghlLocationId,
                isImpersonating: this.nexusUser.isImpersonating
            });

            // Store raw token for API calls
            sessionStorage.setItem('nexus_access_token', payload.accessToken);

            // NOTE: Don't send READY here - already sent from app.js
            // AUTH_TOKEN is received AFTER READY, just store the context

            // Fire registered callback
            if (this._onAuthReady) {
                this._onAuthReady(this.nexusUser);
            }

        } catch (error) {
            console.error('[NexusConfig] Failed to process AUTH_TOKEN:', error);
            this.sendToParent('ERROR', { message: 'Invalid token format' });
        }
    },

    /**
     * Decode JWT payload (no signature validation)
     * @param {string} token - JWT string
     * @returns {Object} Decoded claims
     */
    _decodeJwt(token) {
        const parts = token.split('.');
        if (parts.length !== 3) {
            throw new Error('Invalid JWT format');
        }
        // Decode base64url payload
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const decoded = atob(payload);
        return JSON.parse(decoded);
    },

    /**
     * Handle THEME_CHANGE message from NEXUS parent
     * Applies theme without saving to localStorage (parent controls when embedded)
     */
    _handleThemeChange(payload) {
        if (payload && payload.theme && (payload.theme === 'dark' || payload.theme === 'light')) {
            document.documentElement.setAttribute('data-theme', payload.theme);
            // Update theme toggle icons if they exist
            const iconLight = document.querySelector('.icon-light');
            const iconDark = document.querySelector('.icon-dark');
            if (iconLight && iconDark) {
                iconLight.style.display = payload.theme === 'dark' ? 'none' : 'inline';
                iconDark.style.display = payload.theme === 'dark' ? 'inline' : 'none';
            }
            console.log('[NexusConfig] Theme changed to:', payload.theme);
        }
    },

    /**
     * Register callback for when AUTH_TOKEN is processed (MP-05)
     * @param {Function} callback - Called with nexusUser when auth is ready
     */
    onAuthReady(callback) {
        this._onAuthReady = callback;
        // If already authenticated, call immediately
        if (this.nexusUser) {
            callback(this.nexusUser);
        }
    },

    /**
     * Request AUTH_TOKEN from parent (MP-05)
     * Call this after DOMContentLoaded if embedded to handle race conditions
     */
    requestAuth() {
        if (this.isEmbedded()) {
            console.log('[NexusConfig] Requesting AUTH_TOKEN from parent');
            this.sendToParent('REQUEST_AUTH', {});
        }
    },

    /**
     * Request a FRESH token from the parent and wait for the AUTH_TOKEN reply.
     * Used when a gateway/discovery call returns 401 (expired token). Works in
     * both nexus and standalone embedded modes. Uses its own message listener
     * so it works even from a module instance whose init() never ran.
     * @param {number} timeoutMs - Max wait for the parent's AUTH_TOKEN
     * @returns {Promise<string|null>} Fresh access token, or null on timeout / not embedded
     */
    requestFreshToken(timeoutMs = 5000) {
        return new Promise((resolve) => {
            if (!this.isEmbedded()) {
                resolve(null);
                return;
            }
            let settled = false;
            const onMessage = (event) => {
                const message = event.data;
                if (!message || message.type !== 'AUTH_TOKEN') return;
                if (message.source === 'campaigns') return;
                if (!message.payload || !message.payload.accessToken) return;
                if (settled) return;
                settled = true;
                window.removeEventListener('message', onMessage);
                clearTimeout(timer);
                // Persist to sessionStorage + user context (idempotent if the
                // init() listener already processed the same message)
                this._handleAuthToken(message.payload);
                resolve(message.payload.accessToken);
            };
            const timer = setTimeout(() => {
                if (settled) return;
                settled = true;
                window.removeEventListener('message', onMessage);
                console.error('[NexusConfig] REQUEST_AUTH timed out after', timeoutMs, 'ms - no fresh AUTH_TOKEN received');
                resolve(null);
            }, timeoutMs);
            window.addEventListener('message', onMessage);
            this.sendToParent('REQUEST_AUTH', {});
        });
    },

    /**
     * Run a fetch thunk; on 401 when embedded, obtain a fresh token from the
     * parent (REQUEST_AUTH) and retry the request ONCE. The thunk must build
     * its own headers so the retry picks up the refreshed token.
     * @param {Function} makeRequest - () => Promise<Response>
     * @returns {Promise<Response>}
     */
    async fetchWithAuthRetry(makeRequest) {
        let response = await makeRequest();
        if (response.status === 401 && this.isEmbedded()) {
            console.warn('[NexusConfig] 401 received - requesting fresh AUTH_TOKEN and retrying once');
            const freshToken = await this.requestFreshToken();
            if (freshToken) {
                response = await makeRequest();
            }
        }
        return response;
    },

    /**
     * Get current user context (MP-05)
     * Returns nexusUser if unified identity, null otherwise
     */
    getUser() {
        return this.nexusUser;
    },

    /**
     * Check if unified identity is active (MP-05)
     */
    isUnifiedIdentity() {
        return this.trustNexusJwt && this.nexusUser !== null;
    },

    /**
     * Check if running in an iframe
     */
    isEmbedded() {
        try {
            return window.self !== window.top;
        } catch (e) {
            // Cross-origin iframe access throws SecurityError
            // Log the error but return true (we are embedded if we can't access parent)
            console.error('[CONFIG ERROR] isEmbedded check failed (cross-origin):', e.message);
            return true;
        }
    },

    /**
     * Send postMessage to parent window (NEXUS)
     * Auth handshake (READY, REQUEST_AUTH): sends whenever embedded, in BOTH
     * mode=nexus and mode=standalone — standalone (expert) mode needs the same
     * token refresh path or the token pushed at load expires with no way to renew.
     * Other messages: only send if in NEXUS mode AND embedded.
     */
    sendToParent(type, payload = {}) {
        const embedded = this.isEmbedded();
        const nexusMode = this.isNexusMode();

        // Log state for debugging
        console.log(`[NexusConfig] sendToParent(${type}) - embedded: ${embedded}, nexusMode: ${nexusMode}`);

        // Auth handshake messages fire whenever embedded, regardless of mode
        const isAuthHandshake = type === 'READY' || type === 'REQUEST_AUTH';
        if (isAuthHandshake) {
            if (!embedded) {
                console.log(`[NexusConfig] Not embedded, skipping ${type}`);
                return false;
            }
        } else {
            // Other messages require both NEXUS mode AND embedded
            if (!nexusMode || !embedded) {
                console.log(`[NexusConfig] Skipping ${type} - nexusMode: ${nexusMode}, embedded: ${embedded}`);
                return false;
            }
        }

        const message = {
            type: type,
            source: 'campaigns',
            payload: payload,
            timestamp: Date.now()
        };

        try {
            window.parent.postMessage(message, '*');
            console.log('[NexusConfig] ✅ Sent postMessage:', type, payload);
            return true;
        } catch (e) {
            console.error('[NexusConfig] ❌ Failed to send postMessage:', e);
            return false;
        }
    },

    /**
     * Get base path for navigation when running under NEXUS
     * Returns '/campaigns' when served under /campaigns/ path, empty string otherwise
     *
     * Note: We check path, not just embedding, because:
     * - Direct browser access to /campaigns/* also needs prefix
     * - The path determines nginx routing, not embedding status
     */
    getNavigationBasePath() {
        // If running under /campaigns/ path, navigation needs the prefix
        // This handles both iframe AND direct browser access to NEXUS campaigns
        if (window.location.pathname.startsWith('/campaigns/')) {
            return '/campaigns';
        }
        return '';
    },

    /**
     * Navigate to a path, automatically prefixing with /campaigns if in NEXUS
     * @param {string} path - Path like '/tools/discovery/web/framework-selector.html'
     * @param {Object} params - Query parameters to add
     */
    navigateTo(path, params = {}) {
        const basePath = this.getNavigationBasePath();
        const url = new URL(basePath + path, window.location.origin);

        // Add query params
        Object.entries(params).forEach(([key, value]) => {
            url.searchParams.set(key, value);
        });

        // Preserve mode in URL
        if (this.isNexusMode()) {
            url.searchParams.set('mode', 'nexus');
        } else if (this.isStandaloneMode()) {
            url.searchParams.set('mode', 'standalone');
        }

        // PRESERVE THE PROJECT IDENTIFIER, the same way the mode is preserved.
        //
        // A campaign opened inside a project is reached by a project address, and screens
        // further on need to be able to send the person back into that project. Every hop
        // used to drop it, so by the time the ads screen was reached nothing there knew
        // which project it belonged to. Carried here rather than at each call site, so a
        // new screen cannot forget it.
        if (!url.searchParams.get('nexusId')) {
            var currentNexusId = new URLSearchParams(window.location.search).get('nexusId');
            if (currentNexusId) url.searchParams.set('nexusId', currentNexusId);
        }

        console.log('[NexusConfig] Navigating to:', url.href);
        window.location.href = url.href;
    },

    /**
     * Content type options for NEXUS mode
     * Maps to traffic source categories
     */
    contentTypes: {
        cold: {
            label: 'Cold Outreach',
            description: 'Reaching people who don\'t know you yet',
            trafficSources: ['cold-linkedin', 'cold-email', 'cold-ads']
        },
        warm: {
            label: 'Warm Audience',
            description: 'Reaching people who already know you',
            trafficSources: ['organic', 'existing-audience']
        }
    },

    /**
     * Framework selection constraints
     */
    frameworkConstraints: {
        maxSelections: 1,
        inputType: 'radio'
    }
};
